import Vue from 'vue';
import VueRouter, { Route } from 'vue-router';
import { Locale, RoutePath } from '@adobe/edex/ui/shared/constants';
import { routes } from '@adobe/edex/frontend/router/routes';
import constants from '@adobe/edex/frontend/mixins/constants';
import { getSessionLocale, initVueLocale, setSessionLocale } from '@adobe/edex/ui/shared/frontend/utils/locale';
import { BOOTCAMP, EdexAuth } from '@adobe/edex/ui/shared/config/auth';
import { Analytics } from '@adobe/edex/ui/shared/config/analytics';
import { AnalyticsPageInfo } from '@adobe/edex/ui/shared/types/analytics';
import { IMS } from '@adobe/edex/ui/shared/config/ims';

Vue.use(VueRouter);

export enum ExternalRoutePath {
  max = 'https://max.adobe.com/sessions/educators/',
}

export const router = new VueRouter({
  mode: 'history',
  routes,
});

export function trailingSlashGuard(to: Route, from: Route, next): void {
  const { path, query, hash, meta } = to;
  const nextRoute = { path, query, hash, meta };

  nextRoute.path = `/${nextRoute.path.substr(1).replace(/\/+$/, '')}`;

  if (to.path === nextRoute.path) {
    next();
  } else {
    next(nextRoute);
  }
}

export function bootcampGuard(to: Route, from: Route, next): void {
  if (to?.query?.bootcamp) {
    localStorage.setItem(BOOTCAMP, '1');
  }
  next();
}

export async function localeGuard(to: Route, from: Route, next): Promise<void> {
  const sessionLocale = getSessionLocale();
  const { name, params, meta, hash, query } = to;
  const urlLocale = params[constants.localeParam] as Locale;
  let locale: Locale = urlLocale || sessionLocale || Locale.en;

  // url locale param has priority - override
  if (sessionLocale && locale !== sessionLocale) {
    setSessionLocale(locale);
  }

  const nextRoute = {
    name,
    meta,
    hash,
    query,
    params: {
      ...params,
    },
  };

  // if url param is not defined - set if not default
  if (!urlLocale && locale !== Locale.en) {
    nextRoute.params[constants.localeParam] = locale;
  }

  // if `en` or anything else - override with default
  else if (urlLocale && ![Locale.de, Locale.jp].includes(locale)) {
    nextRoute.params[constants.localeParam] = null;
    locale = Locale.en;
  }
  await initVueLocale(locale as Locale);
  if (nextRoute.params[constants.localeParam] === params[constants.localeParam]) {
    next();
  } else {
    next(nextRoute);
  }
}

function getPageInfoFromRoute(route: Route): AnalyticsPageInfo {
  // Locale code, no geolocation available, using user defined preference or en-US by default
  let language = route.params[constants.localeParam] || getSessionLocale() || 'en-US';

  if (language === 'en') {
    language = 'en-US';
  } else if (language === 'de') {
    language = 'de-DE';
  } else if (language === 'ja') {
    language = 'ja-JP';
  }

  // Site section
  let siteSection = '';
  switch (route.name) {
    case 'landing':
      siteSection = 'Home';
      break;
    case 'browse':
      siteSection = 'Search';
      break;
    case 'static-page':
      siteSection = 'Curated Landing Pages';
      break;
    case 'community':
    case 'leaderboard':
    case 'community-non-wildcard':
      siteSection = 'Community';
      break;
    case 'discussion':
      siteSection = 'Discussions';
      break;
    case 'member-profile':
    case 'member-settings':
      siteSection = 'Member';
      break;
    case 'help-center':
      siteSection = 'Other';
      break;
    default:
      siteSection = null;
      break;
  }

  // special cases
  if (route.path.includes(`${RoutePath.professionalLearning}/`)) {
    siteSection = 'Professional Learning';
  }
  if (route.path.includes(`${RoutePath.dashboard}/`)) {
    siteSection = 'Member';
  }
  if (route.path.includes('/code-of-conduct')) {
    siteSection = 'Other';
  }

  const pageInfo: AnalyticsPageInfo = {
    pageName: route.fullPath,
    siteSection,
    language,
  };

  return pageInfo;
}

export function trackRoute(to: Route) {
  // Separate try catch for AA
  try {
    const pageInfo = getPageInfoFromRoute(to);
    Analytics.getInstance().pageView(pageInfo);
  } catch (err) {
    window.newrelic?.noticeError(err);
  }
}

export async function authGuard(to: Route, from: Route, next) {
  if (!to.matched.length) {
    next('/not-found');
  } else {
    if (!to.meta.public) {
      const authOK = await IMS.isAuthenticated();
      if (!authOK) {
        await EdexAuth.signIn();
        return next(false);
      }
    }
    next();
  }
}

export function analyticsPageViewHook(to: Route, from: Route) {
  if (to.fullPath === from.fullPath) {
    return;
  }
  // StaticPage handles tracking conditionally
  if (to.name !== 'static-page') {
    trackRoute(to);
  }
}

router.beforeEach(bootcampGuard);
router.beforeEach(trailingSlashGuard);
router.beforeEach(localeGuard);
// Prevent access to routes that are not public
router.beforeEach(authGuard);
router.afterEach(analyticsPageViewHook);
